import { redirect } from "react-router";
import { apiClient } from "~/api/api-client";
import { TENANT_ID_HEADER } from "~/constants/tenant";
import { env } from "~/env";
import { getTenantIdFromPathname } from "~/state/tenant";
import { authService } from "./auth-service";
import { CSRF_TOKEN_COOKIE, getCookie, setCookie } from "./cookie";
import { EMBED_AUTH_WAIT_MS, isEmbedModeSearchParams } from "./embed-utils";

async function fetchCsrfToken(tenantId: string | null) {
  const options = tenantId
    ? {
        headers: {
          [TENANT_ID_HEADER]: tenantId,
        },
      }
    : undefined;

  const { data, error } = options
    ? await apiClient.GET("/auth/csrf-token", options)
    : await apiClient.GET("/auth/csrf-token");

  const fetchSucceeded = !error && Boolean(data?.csrf_token);
  if (fetchSucceeded) {
    setCookie(CSRF_TOKEN_COOKIE, data.csrf_token);
  }

  if (env.VITE_CSRF_TOKEN_OVERRIDE) {
    setCookie(CSRF_TOKEN_COOKIE, env.VITE_CSRF_TOKEN_OVERRIDE);
    return;
  }

  if (!fetchSucceeded) {
    throw new Error("Failed to fetch CSRF token");
  }
}

/**
 * Tenant scope (tenant id, or "" outside tenant routes) the current CSRF
 * cookie was fetched for in this page session.
 */
let csrfTokenScope: string | null = null;
let csrfTokenRequest: { scope: string; promise: Promise<void> } | null = null;

/**
 * Ensure a CSRF token cookie exists for the current tenant.
 *
 * Fetched once per tenant per page load (and again if the cookie disappears),
 * not on every navigation: middleware runs before every client navigation,
 * including search-param-only ones, so fetching each time put a network round
 * trip in front of every filter change and drawer open. Concurrent navigations
 * share one request.
 */
async function ensureCsrfToken(pathname: string) {
  const tenantId = getTenantIdFromPathname(pathname);
  const scope = tenantId ?? "";

  if (csrfTokenScope === scope && getCookie(CSRF_TOKEN_COOKIE)) {
    return;
  }
  if (csrfTokenRequest?.scope === scope) {
    return csrfTokenRequest.promise;
  }

  const promise = fetchCsrfToken(tenantId).then(() => {
    csrfTokenScope = scope;
  });
  csrfTokenRequest = { scope, promise };
  try {
    await promise;
  } finally {
    if (csrfTokenRequest?.promise === promise) csrfTokenRequest = null;
  }
}

/** Test helper: forget the cached CSRF token scope. */
export const resetCsrfTokenCache = () => {
  csrfTokenScope = null;
  csrfTokenRequest = null;
};

// Runs before loaders and before tenant-context-middleware
// Ensures user is authenticated before setting tenant context
export const authMiddleware = async ({ request }: { request: Request }) => {
  await authService.ensureInitialized();

  const url = new URL(request.url);
  if (url.pathname === "/auth/callback") {
    return;
  }
  if (!authService.isAuthenticated()) {
    if (isEmbedModeSearchParams(url.searchParams)) {
      await authService.waitForEmbedToken(EMBED_AUTH_WAIT_MS);
    }
    if (!authService.isAuthenticated()) {
      throw redirect(authService.getLoginUrl() || "/");
    }
  }

  await ensureCsrfToken(url.pathname);
};
